// Espelha app/services/exam_objective_scoring.py

export const OBJECTIVE_SCORE_MODE_WEIGHTED_AVERAGE = 'weighted_average';
export const OBJECTIVE_SCORE_MODE_WEIGHTED_SUM = 'weighted_sum';

export function normalizeObjectiveScoreMode(mode) {
  const m = String(mode || '').toLowerCase();
  if (m === OBJECTIVE_SCORE_MODE_WEIGHTED_SUM) return OBJECTIVE_SCORE_MODE_WEIGHTED_SUM;
  return OBJECTIVE_SCORE_MODE_WEIGHTED_AVERAGE;
}

function rowFields(row) {
  const area = String(row?.area || '').trim();
  const w = parseFloat(String(row?.weight ?? '').replace(',', '.')) || 0;
  const qc = parseInt(String(row?.question_count ?? row?.questions ?? '').trim(), 10) || 0;
  return { area, w, qc: Math.max(0, qc) };
}

export function objectiveScoreModeLabel(mode) {
  return normalizeObjectiveScoreMode(mode) === OBJECTIVE_SCORE_MODE_WEIGHTED_SUM
    ? 'Soma (acertos × peso)'
    : 'Média ponderada (acertos × peso)';
}

/** Texto da coluna Nota objetiva (inclui nota calculada mesmo com zero em área). */
export function formatExamObjectiveScoreDisplay(finalScore, scoreMode, disqualifiedZero) {
  const fmtPts = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} pts`;
  };
  const pts = fmtPts(finalScore);
  if (!pts && !disqualifiedZero) {
    return { primary: '—', secondary: null, title: '' };
  }
  if (disqualifiedZero) {
    return {
      primary: pts || '—',
      secondary: '0 em área*',
      title: pts
        ? `Nota calculada (OMR): ${pts}. Regra do processo: desclassificado por zero acertos em área com peso.`
        : 'Zero acertos em área com peso (regra do processo).',
    };
  }
  return { primary: pts || '—', secondary: null, title: '' };
}

export function computeExamFinalPreview(weights, areaScores, scoreMode) {
  const mode = normalizeObjectiveScoreMode(scoreMode);
  if (!Array.isArray(weights) || !areaScores || typeof areaScores !== 'object') return null;

  let totalW = 0;
  let acc = 0;
  let used = false;

  for (const row of weights) {
    const { area, w, qc } = rowFields(row);
    if (!area || w <= 0) continue;
    const raw = areaScores[area];
    if (raw === undefined || raw === null || String(raw).trim() === '') return null;
    const acertos = parseFloat(String(raw).replace(',', '.'));
    if (Number.isNaN(acertos) || acertos < 0) return null;
    if (qc > 0 && acertos > qc) return null;

    acc += acertos * w;
    totalW += w;
    used = true;
  }

  if (!used) return null;
  if (mode === OBJECTIVE_SCORE_MODE_WEIGHTED_SUM) {
    return Math.round(acc * 10000) / 10000;
  }
  if (totalW <= 0) return null;
  return Math.round((acc / totalW) * 10000) / 10000;
}

export function objectiveFinalDisplay(final, scoreMode, _objectiveMaxPoints, hasEssay) {
  if (final == null || Number.isNaN(final)) return '—';
  const fmt = (n) => Number(n).toFixed(2).replace('.', ',');
  const modeLabel = normalizeObjectiveScoreMode(scoreMode) === OBJECTIVE_SCORE_MODE_WEIGHTED_SUM ? 'soma' : 'média';
  let text = `${fmt(final)} pts objetiva (${modeLabel} acertos × peso)`;
  if (hasEssay) text += ' · soma redação ao salvar';
  return text;
}

export function gradeEntryHelpText(scoreMode) {
  if (normalizeObjectiveScoreMode(scoreMode) === OBJECTIVE_SCORE_MODE_WEIGHTED_SUM) {
    return 'Informe o número de acertos em cada área (valor absoluto, de 0 até a quantidade de questões da área). A nota objetiva será a soma dos acertos × peso de cada área.';
  }
  return 'Informe o número de acertos em cada área (valor absoluto). A nota objetiva será a média ponderada dos produtos (acertos × peso): Σ(acertos×peso) ÷ Σ(peso).';
}

export function savedGradesNotificationText(scoreMode) {
  return normalizeObjectiveScoreMode(scoreMode) === OBJECTIVE_SCORE_MODE_WEIGHTED_SUM
    ? 'Nota objetiva (soma acertos × peso) registrada.'
    : 'Nota objetiva (média acertos × peso) registrada.';
}

export function areaScoreMax(row) {
  const qc = parseInt(String(row?.question_count ?? row?.questions ?? '').trim(), 10) || 0;
  return qc > 0 ? qc : null;
}

/** Garante áreas do Gabarito e modo de cálculo ao abrir o modal (lista pode vir sem pesos). */
export function enrichApplicationForGradeModal(app, processMeta) {
  if (!app) return null;
  const fromApp = Array.isArray(app.course_exam_area_weights) ? app.course_exam_area_weights : [];
  const fromProc = Array.isArray(processMeta?.exam_area_weights) ? processMeta.exam_area_weights : [];
  const weights = fromApp.length > 0 ? fromApp : fromProc;
  return {
    ...app,
    course_exam_area_weights: weights,
    exam_objective_score_mode:
      app.exam_objective_score_mode || processMeta?.exam_objective_score_mode || 'weighted_average',
    exam_uses_essay: Boolean(app.exam_uses_essay ?? processMeta?.exam_uses_essay),
    exam_objective_max_points: app.exam_objective_max_points ?? processMeta?.exam_objective_max_points,
  };
}
